import type { Owner } from '@paintscout/api'
import find from 'lodash/find'
import type { OptionsDocument, PublicQuote, QuoteDocument, RatesDocument } from 'paintscout'
import omit from 'lodash/omit'
import moment from 'moment'
import { mapPublicToLeadSource, mapPublicToTagDetails, mapPublicToTags } from '../../../options'
import type { QuoteTemplateOption, QuoteTypeOption } from '../../../options'
import { createQuote } from '../../quotes/create-quote/create-quote'

import { numberFields } from '../../../util'
import { createLineItem, updateLineItem } from '../../line-items'
import { mapPublicToContact } from '../map-public-to-contact'

export function mapPublicToQuote(args?: {
  publicQuote: Partial<PublicQuote>
  options: OptionsDocument
  owner: Owner
  template?: QuoteTemplateOption
  signature?: string
  rates?: RatesDocument
}): QuoteDocument {
  const { options, rates, owner, publicQuote, template, signature } = args

  /*
    If the quoteType is provided with a value, we should use that.
    Otherwise, if a label is provided, we'll try to find that type.
    Lastly we also have a quoteTypeId field we will check
  */
  const quoteType = getQuoteType({ options, publicQuote })

  const jobAddress = {
    address: (publicQuote?.jobAddress?.address ?? '').trim(),
    city: (publicQuote?.jobAddress?.city ?? '').trim(),
    province: (publicQuote?.jobAddress?.state ?? '').trim(),
    postal: (publicQuote?.jobAddress?.zip ?? '').trim()
  }
  const isEmptyJobAddress = Object.values(jobAddress).filter((addressValue) => addressValue !== '').length === 0

  const contact = publicQuote.contact ? mapPublicToContact({ contact: publicQuote.contact, options, owner }) : null

  const quoteDate =
    typeof publicQuote?.dates?.quote === 'number'
      ? publicQuote.dates.quote
      : moment(publicQuote?.dates?.quote).unix() * 1000

  const sourceNumberFields = [
    'dealId',
    'projectId',
    'estimateId',
    'customerId',
    'invoiceId',
    'locationId',
    'jobTypeId',
    'campaignTypeId'
  ]
  let quote = {
    ...createQuote({
      options,
      rates,
      quoteType,
      template,
      owner,
      contact,
      invoice: publicQuote?.isInvoice ?? false,
      quoteDate,
      estimatorNotes: publicQuote?.estimatorNotes ?? publicQuote.estimatorNotesHtml,
      clientNote: publicQuote?.clientNote
        ? publicQuote.clientNote
        : publicQuote.clientNoteHtml
          ? publicQuote.clientNoteHtml
          : '',
      crewNote: publicQuote?.crewNote ?? publicQuote.crewNoteHtml,
      jobIdentifier: (publicQuote?.jobAddress?.identifier ?? '').trim(),
      jobAddress: isEmptyJobAddress ? null : jobAddress,
      hourlyRate: publicQuote?.hourlyRate,
      source: publicQuote?.source
        ? {
            // If we dont inline these omit arrays, we dont get type checking on the fields -.-
            // so doing this so we remember to add new fields to this conversion, need to add to sourceNumberFields as well
            ...omit(publicQuote.source, [
              'dealId',
              'projectId',
              'estimateId',
              'customerId',
              'invoiceId',
              'locationId',
              'jobTypeId',
              'campaignTypeId'
            ]),
            ...numberFields({
              obj: publicQuote.source,
              fields: sourceNumberFields
            })
          }
        : null,
      sources: publicQuote?.sources
        ? {
            ...Object.fromEntries(
              publicQuote.sources.map((source) => [
                source.provider,
                {
                  ...omit(source, [
                    'dealId',
                    'projectId',
                    'estimateId',
                    'customerId',
                    'invoiceId',
                    'locationId',
                    'jobTypeId',
                    'campaignTypeId'
                  ]),
                  ...numberFields({
                    obj: source,
                    fields: sourceNumberFields
                  })
                }
              ])
            )
          }
        : null,
      signature
    }),
    parent_quote: publicQuote?.parentQuoteId ?? null,
    parent_quote_number: publicQuote?.parentQuoteNumber ?? null,
    child_invoice: publicQuote?.childInvoiceId ?? null,
    child_invoice_number: publicQuote?.childInvoiceNumber ?? null,
    child_invoices: publicQuote?.childInvoiceIds ?? [],
    child_invoice_numbers: publicQuote?.childInvoiceNumbers ?? [],
    deal_id: publicQuote?.dealId ?? null
  } as QuoteDocument

  if (publicQuote.leadSource) {
    quote.lead_source = mapPublicToLeadSource({ options, leadSource: publicQuote.leadSource })
  }

  // Map tags string to array
  if (publicQuote.tags) {
    if (typeof publicQuote.tags === 'string') {
      publicQuote.tags = (publicQuote.tags as string).split(',').map((tag) => tag.trim())
    }
    quote.tags = mapPublicToTags({ options, tags: publicQuote.tags.filter(Boolean) })
  }

  // Map tagDetails from PublicTagDetails to Tag format
  if (publicQuote.tagDetails) {
    quote.tagDetails = mapPublicToTagDetails({
      options,
      tagDetails: publicQuote.tagDetails
    })
  }

  if (!template) {
    if (publicQuote.tax) {
      // If given details, build from those
      if (publicQuote.tax.details.length) {
        const taxDetails = publicQuote.tax.details.reduce((acc, detail) => {
          // Accept descriptions or key matches, or include custom
          if (detail.key === 'custom') {
            return [
              ...acc,
              {
                ...detail
              }
            ]
          }

          const taxRate = Object.values(options.options?.taxRates ?? {}).find((rate) =>
            [rate.key, rate.description].includes(detail.key)
          )

          if (!taxRate) {
            return acc
          } else {
            return [
              ...acc,
              {
                ...taxRate
              }
            ]
          }
        }, [])

        quote.tax_rate = taxDetails.reduce((acc, detail) => acc + Number(detail.rate), 0)
        quote.tax_description = taxDetails.map((detail) => detail.description).join(', ')
        quote.tax_details = taxDetails
      } else {
        // else if generic, we just adapt it
        quote.tax_rate = publicQuote.tax.rate ?? 0
        quote.tax_description = publicQuote.tax.description ?? ''
        quote.tax_details = [{ key: 'taxRate', rate: quote.tax_rate, description: quote.tax_description }]
      }
    } else {
      quote.tax_rate = options.options?.taxRate?.value ?? 0
      quote.tax_description = options.options?.taxRate?.description ?? ''
      quote.tax_details = options.options?.taxRate?.details ?? null
    }

    quote.discount = publicQuote.discount?.rate ?? 0
    quote.discount_type = { value: publicQuote.discount?.type ?? 'percentage' }
    quote.discount_description = publicQuote.discount?.description ?? ''
  }

  if (publicQuote.items) {
    publicQuote.items.forEach((publicLineItem) => {
      const item = createLineItem({})
      if (publicLineItem.name) {
        item.label = publicLineItem.name
      }
      if (publicLineItem.descriptionHtml) {
        item.description = publicLineItem.descriptionHtml
      } else if (publicLineItem.description) {
        item.description = publicLineItem.description
      }
      if (publicLineItem.price) {
        item.price = publicLineItem.price
      }

      quote = updateLineItem({ quote, item, calculate: false, options })
    })
  }

  if (publicQuote.additionalWorkItems) {
    publicQuote.additionalWorkItems.forEach((publicLineItem) => {
      const item = createLineItem({})

      if (publicLineItem.name) {
        item.name = publicLineItem.name
      }
      if (publicLineItem.descriptionHtml) {
        item.description = publicLineItem.descriptionHtml
      } else if (publicLineItem.description) {
        item.description = publicLineItem.description
      }
      if (publicLineItem.price) {
        item.price = publicLineItem.price
      }

      item.section = 'additional'

      quote = updateLineItem({ quote, item, calculate: false, options })
    })
  }

  if (publicQuote.payments) {
    quote.payments = publicQuote.payments
  }

  quote.totalAdjustment = publicQuote.totalAdjustment

  return quote
}

function getQuoteType({ options, publicQuote }) {
  /*
    Priorities of quoteTypes:
      1. If value is present, and exists in options, use it.
      2. If mixed is present, and exists in options (as a value), use it.
      3. If mixed is present, and exists in options (as a label), use it.
      4. If label is present, and exists in options (as a label), use it.
      5. If quoteTypeId is present, and exists in options (as a key), use it.
      6. All fail, return null

    * Note: This very similar to findTemplate in getTemplate, so
      if you make changes - they should probably be reflected there.

  */
  const quoteTypeOptions = options.options?.intExtOptions?.values ?? {}
  const { quoteType, quoteTypeId } = publicQuote

  if (quoteType?.value && quoteTypeOptions[quoteType.value]) {
    return quoteTypeOptions[quoteType.value].value
  }

  if (quoteType?.mixed && quoteTypeOptions[quoteType.mixed]) {
    return quoteTypeOptions[quoteType.mixed].value
  }

  if (quoteType?.mixed) {
    const foundOption = find<QuoteTypeOption>(quoteTypeOptions, { label: quoteType.mixed })
    if (foundOption) {
      return foundOption.value
    }
  }

  if (quoteType?.label) {
    const foundOption = find<QuoteTypeOption>(quoteTypeOptions, { label: quoteType.label })
    if (foundOption) {
      return foundOption.value
    }
  }

  if (quoteTypeId && quoteTypeOptions[quoteTypeId]) {
    return quoteTypeOptions[quoteTypeId].value
  }

  return null
}
